/**
 * slingbelt_productInfo_InfiniteScroll.js
 *
 * 【機能概要】
 * Shopify 商品ページで、メディアカラム (.product__media-wrapper) の高さが
 * 情報カラム (.product__info-container) より大きい場合に、
 * スクロールに応じて情報カラムを段階的に下へ移動させ、メディアカラムに追従させる。
 *
 * 主な機能:
 *   - 画面幅 1024px 超のときのみ動作。1024px 以下では全スタイルをリセットして通常レイアウトに戻す
 *   - メディア高さに応じて段数を自動計算し、wrapper に必要なスクロール空間を確保
 *   - 下スクロール時: 情報カラムがヘッダー下端より上に消えたら次の段へ translateY で移動
 *   - 上スクロール時: 情報カラム上端がビューポート70%ラインを超えたら前の段へ戻す
 *   - 段切り替え時にフェードアウト → 移動 → フェードインの演出を実行
 *   - isAnimating フラグで段切り替え中の二重実行を防止
 *   - requestAnimationFrame でスクロール処理をスロットリング
 *   - ResizeObserver で要素の高さ変化を監視し、随時再計算
 *   - DOMContentLoaded + ダブル rAF で描画確定後に初期化。要素未存在時はリトライ
 *   - Shopify の shopify:section:load イベントにも対応し、セクション再描画後も動作を維持
 *
 * 定数は先頭部分で一括管理しており、GAP・FADE_DURATION などは自由に調整可能。
 */

(function () {
  'use strict';

  // ============================================================
  // 定数 (調整可能)
  // ============================================================
  var GAP              = 25;   // 段間のギャップ (px)
  var FADE_DURATION    = 300;  // フェード時間 (ms)
  var BREAKPOINT       = 1024; // このpx以下では処理を無効化
  var UP_TRIGGER_RATIO = 0.7;  // 上スクロール戻し判定: ビューポート高さの割合

  // ============================================================
  // 要素参照
  // ============================================================
  var infoContainer = null;
  var infoWrapper   = null;
  var mediaWrapper  = null;
  var header        = null;

  // ============================================================
  // 状態変数
  // ============================================================
  var currentPage    = 0;     // 現在の段 (0 = 初期段)
  var totalPages     = 1;     // 総段数
  var infoHeight     = 0;     // infoContainer の高さ (px)
  var isAnimating    = false; // フェードアニメーション中フラグ
  var isActive       = false; // 1024px 超で有効化されているか
  var rafId          = null;  // rAF スロットリング用 ID
  var resizeObserver = null;  // ResizeObserver インスタンス

  // ============================================================
  // DOM 要素の取得
  // ============================================================
  function getElements() {
    infoContainer = document.querySelector('.product__info-container');
    infoWrapper   = document.querySelector('.product__info-wrapper');
    mediaWrapper  = document.querySelector('.product__media-wrapper');
    header        = document.querySelector('.sb25_header');
    return !!(infoContainer && infoWrapper && mediaWrapper && header);
  }

  // ============================================================
  // 段数を計算する
  // 仕様:
  //   mediaHeight >= infoHeight * 1.6 → pages = 2
  //   mediaHeight >= infoHeight * 2.6 → pages = 3  ...
  // 一般化: pages = max(1, floor(ratio - 0.6) + 1)
  // ============================================================
  function calcPages(mediaH, infoH) {
    if (infoH <= 0) return 1;
    var ratio = mediaH / infoH;
    return Math.max(1, Math.floor(ratio - 0.6) + 1);
  }

  // ============================================================
  // 指定した段への translateY を適用する
  // withFade = true のときフェードアウト → 移動 → フェードイン
  // ============================================================
  function applyTransform(targetPage, withFade) {
    var targetY = targetPage * (infoHeight + GAP);

    if (withFade) {
      isAnimating = true;

      // フェードアウト
      infoContainer.style.transition = 'opacity ' + (FADE_DURATION / 1000) + 's ease';
      infoContainer.style.opacity    = '0';

      setTimeout(function () {
        // 位置を更新
        infoContainer.style.transform = 'translateY(' + targetY + 'px)';

        // 次フレームで opacity を戻す (ブラウザが描画を確定させてからフェードイン)
        requestAnimationFrame(function () {
          infoContainer.style.opacity = '1';
        });

        // フェードイン完了後にアニメーションフラグを解除
        setTimeout(function () {
          isAnimating = false;
        }, FADE_DURATION);

      }, FADE_DURATION);

    } else {
      // 初期化・リサイズ時: トランジションなしで即適用
      infoContainer.style.transition = 'none';
      infoContainer.style.transform  = 'translateY(' + targetY + 'px)';
    }
  }

  // ============================================================
  // wrapper の高さと infoContainer 位置を再計算する
  // リサイズ時・高さ変化時にも呼ばれる
  // ============================================================
  function recalculate() {
    if (!isActive || !getElements()) return;

    infoHeight = infoContainer.offsetHeight;
    var mediaHeight = mediaWrapper.offsetHeight;

    totalPages = calcPages(mediaHeight, infoHeight);

    if (totalPages > 1) {
      // 段送りあり → infoContainer を absolute で配置し、wrapper にスクロール空間を確保
      infoWrapper.style.position   = 'relative';
      infoContainer.style.position = 'absolute';
      infoContainer.style.top      = '0';
      infoContainer.style.left     = '0';
      infoContainer.style.right    = '0';

      // wrapperHeight = infoHeight * pages + gap * (pages - 1)
      var wrapperHeight = infoHeight * totalPages + GAP * (totalPages - 1);
      // 算出した高さが mediaWrapper の高さを下回る場合は固定高さを適用しない
      if (wrapperHeight >= mediaHeight) {
        infoWrapper.style.height = wrapperHeight + 'px';
      } else {
        infoWrapper.style.height = '';
      }
    } else {
      // 段送り不要 → infoContainer は relative (通常フローに近い形)
      infoWrapper.style.position   = '';
      infoContainer.style.position = 'relative';
      infoContainer.style.top      = '';
      infoContainer.style.left     = '';
      infoContainer.style.right    = '';
      infoWrapper.style.height     = '';
    }

    // 現在の段が新しい totalPages を超えていれば補正
    if (currentPage >= totalPages) {
      currentPage = totalPages - 1;
    }
    applyTransform(currentPage, false);
  }

  // ============================================================
  // スクロール時の段移動判定
  // rAF 経由でスロットリングされて呼ばれる
  // ============================================================
  function onScroll() {
    if (!isActive || isAnimating || !infoContainer) return;

    var rect         = infoContainer.getBoundingClientRect();
    var headerHeight = header ? header.offsetHeight : 0;
    var vh           = window.innerHeight;

    // --- 下スクロール: 次の段へ ---
    // infoContainer の下端がヘッダー下端より上に消えたら次の段へ移動
    if (currentPage < totalPages - 1) {
      if (rect.bottom < headerHeight) {
        currentPage++;
        applyTransform(currentPage, true);
        return;
      }
    }

    // --- 上スクロール: 前の段へ戻す ---
    // infoContainer の上端がビューポート高さ×70% ラインより下になったら前の段へ戻す
    if (currentPage > 0) {
      if (rect.top > vh * UP_TRIGGER_RATIO) {
        currentPage--;
        applyTransform(currentPage, true);
        return;
      }
    }
  }

  // ============================================================
  // スクロールイベントハンドラ (rAF でスロットリング)
  // ============================================================
  function handleScroll() {
    if (rafId !== null) return;
    rafId = requestAnimationFrame(function () {
      rafId = null;
      onScroll();
    });
  }

  // ============================================================
  // 有効化: 1024px 超のときに呼ぶ
  // ============================================================
  function activate() {
    if (isActive) return;
    if (!getElements()) return;

    isActive = true;

    // position は recalculate() 内で totalPages に応じて設定する
    recalculate();

    window.addEventListener('scroll', handleScroll, { passive: true });

    // ResizeObserver で infoContainer・mediaWrapper の高さ変化を監視
    if (resizeObserver) {
      resizeObserver.observe(infoContainer);
      resizeObserver.observe(mediaWrapper);
    }
  }

  // ============================================================
  // 無効化: 1024px 以下になったときにすべてリセット
  // ============================================================
  function deactivate() {
    if (!isActive) return;
    isActive = false;

    window.removeEventListener('scroll', handleScroll);

    if (rafId !== null) {
      cancelAnimationFrame(rafId);
      rafId = null;
    }

    if (resizeObserver) {
      resizeObserver.disconnect();
    }

    // スタイルをすべてリセットして通常レイアウトに戻す
    if (infoWrapper) {
      infoWrapper.style.position = '';
      infoWrapper.style.height   = '';
    }
    if (infoContainer) {
      infoContainer.style.position   = '';
      infoContainer.style.top        = '';
      infoContainer.style.left       = '';
      infoContainer.style.right      = '';
      infoContainer.style.transform  = '';
      infoContainer.style.opacity    = '';
      infoContainer.style.transition = '';
    }

    // 状態をリセット
    currentPage = 0;
    isAnimating = false;
  }

  // ============================================================
  // ブレークポイント判定: 初期化時・リサイズ時に呼ぶ
  // ============================================================
  function checkBreakpoint() {
    if (window.innerWidth > BREAKPOINT) {
      if (!isActive) {
        activate();
      } else {
        // すでに有効なら再計算だけ実行
        recalculate();
      }
    } else {
      if (isActive) {
        getElements();
        deactivate();
      }
    }
  }

  // ============================================================
  // 初期化: 要素が存在し描画確定後に1度だけ呼ぶ
  // ============================================================
  function init() {
    if (!getElements()) {
      // 要素がまだ存在しない場合は少し待ってリトライ
      setTimeout(function () {
        requestAnimationFrame(init);
      }, 100);
      return;
    }

    // ResizeObserver を準備 (サポートされていれば)
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(function () {
        if (isActive) {
          requestAnimationFrame(recalculate);
        }
      });
    }

    checkBreakpoint();

    // リサイズ時に再判定
    window.addEventListener('resize', function () {
      requestAnimationFrame(checkBreakpoint);
    }, { passive: true });
  }

  // ============================================================
  // エントリーポイント: ダブル rAF で描画確定を待ってから初期化
  // ============================================================
  function startWhenReady() {
    requestAnimationFrame(function () {
      requestAnimationFrame(init);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', startWhenReady);
  } else {
    startWhenReady();
  }

  // ============================================================
  // Shopify セクション再描画への対応
  // ============================================================
  document.addEventListener('shopify:section:load', function () {
    currentPage    = 0;
    isAnimating    = false;
    isActive       = false;
    resizeObserver = null;
    startWhenReady();
  });

})();
